/**
 * Hook that builds a NavigationContext from current auth, org, and subscription state.
 * Shared by useNavigationItems and useCommands to avoid duplicating context construction.
 */

import { useContext, useMemo } from 'react';
import type { NavigationContext } from '../lib/navigation/rules/NavigationRule';
import { AuthContext } from '../context/auth.context';
import { useCurrentOrg } from './useCurrentOrg';
import { useUserPermissions } from './useUserPermissions';
import { useSubscription } from './useSubscription';
import { useMyMemberships } from './useMyMemberships';

export function useNavigationContext(): {
  context: NavigationContext;
  subscriptionLoading: boolean;
  authLoading: boolean;
} {
  // Safe context access — returns null/undefined when providers are absent (admin portal)
  const authContext = useContext(AuthContext);
  const user = authContext?.user ?? null;
  const authLoading = authContext?.loading ?? false;
  const { currentOrg } = useCurrentOrg();
  const { hasPermission, isSystemAdmin } = useUserPermissions();
  // Reuse useSubscription which handles 402 as null data (not an error)
  const { data: subscription, isLoading: subscriptionLoading } = useSubscription();
  // Managed status comes from the already-loaded /me/organizations enrichment;
  // no extra fetch. Match the current org against its membership row.
  const { memberships } = useMyMemberships();

  const context: NavigationContext = useMemo(() => {
    const subscriptionStatus = subscription?.subscriptionStatus;
    const isManaged =
      currentOrg !== null &&
      (memberships.find((m) => m.id === currentOrg.id)?.isManagedByPartner ?? false);

    return {
      hasPermission,
      isSystemAdmin,
      currentOrg: currentOrg
        ? { id: currentOrg.id, name: currentOrg.name, slug: currentOrg.slug }
        : null,
      user,
      hasActiveSubscription: () =>
        subscriptionStatus === 'active' || subscriptionStatus === 'trialing',
      hasFeature: (featureKey: string) => subscription?.features?.[featureKey] === true,
      isManaged,
    };
  }, [hasPermission, isSystemAdmin, currentOrg, user, subscription, memberships]);

  return { context, subscriptionLoading, authLoading };
}
